import { useTranslation } from 'react-i18next'
import CreatableSelect from 'react-select/creatable'

export const emptyProductForm = {
	name: '',
	sku: '',
	article_number: '',
	price: '',
	taxPercent: ''
}

export const getFormPrice = product => {
	if (product?.price == null || product?.price === '') return ''
	if (typeof product.price === 'object' && product.price.$numberDecimal != null) {
		return product.price.$numberDecimal
	}
	return product.price
}

export const buildProductFormState = (product = null) => ({
	name: product?.name ?? '',
	sku: product?.sku ?? '',
	article_number: product?.article_number ?? '',
	price: getFormPrice(product),
	taxPercent: product?.taxPercent ?? ''
})

const inputClassName =
	'w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'

const labelClassName = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600'

export default function ProductFormModal({
	isOpen,
	formMode,
	formState,
	onFieldChange,
	onClose,
	onSubmit,
	isSaving
}) {
	const { t } = useTranslation()

	const articleNumberOptions = [
		{ value: '', label: t('settingsPage.products.fields.article_numberNone') },
		{ value: '43', label: '43' },
		{ value: '113', label: '113' }
	]

	const selectedArticleNumberOption = formState.article_number
		? { value: String(formState.article_number), label: String(formState.article_number) }
		: articleNumberOptions[0]

	if (!isOpen) return null

	return (
		<div className="fixed inset-0 z-20 flex items-center justify-center bg-black/30 px-4 py-6">
			<div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-2xl">
				<div className="mb-4 flex items-center justify-between">
					<h3 className="text-lg font-semibold">
						{formMode === 'add'
							? t('settingsPage.products.form.addTitle')
							: t('settingsPage.products.form.editTitle')}
					</h3>
					<button
						type="button"
						onClick={onClose}
						className="text-sm text-gray-500 hover:text-gray-900"
					>
						{t('notifications.cancelButton')}
					</button>
				</div>
				<form onSubmit={onSubmit} className="space-y-4">
					<div>
						<label className={labelClassName}>{t('settingsPage.products.fields.name')}</label>
						<input
							type="text"
							value={formState.name}
							required
							onChange={e => onFieldChange('name', e.target.value)}
							className={inputClassName}
						/>
					</div>
					<div className="grid gap-4 md:grid-cols-2">
						<div>
							<label className={labelClassName}>{t('settingsPage.products.fields.sku')}</label>
							<input
								type="text"
								value={formState.sku}
								onChange={e => onFieldChange('sku', e.target.value)}
								className={inputClassName}
							/>
						</div>
						<div>
							<label className={labelClassName}>{t('settingsPage.products.fields.price')}</label>
							<input
								type="number"
								step="0.01"
								value={formState.price}
								onChange={e => onFieldChange('price', e.target.value)}
								className={inputClassName}
							/>
						</div>
					</div>
					<div className="grid gap-4 md:grid-cols-2">
						<div>
							<label className={labelClassName}>{t('settingsPage.products.fields.taxPercent')}</label>
							<input
								type="number"
								step="0.01"
								min="0"
								value={formState.taxPercent}
								onChange={e => onFieldChange('taxPercent', e.target.value)}
								className={inputClassName}
							/>
						</div>
						<div>
							<label className={labelClassName}>{t('settingsPage.products.fields.article_number')}</label>
							<CreatableSelect
								name="article_number"
								options={articleNumberOptions}
								value={selectedArticleNumberOption}
								onChange={option => onFieldChange('article_number', option?.value ?? '')}
								onCreateOption={value => onFieldChange('article_number', value)}
								placeholder={t('settingsPage.products.fields.article_numberPlaceholder')}
								formatCreateLabel={value =>
									t('settingsPage.products.fields.createArticleNumber', { value })
								}
								className="react-select"
								classNamePrefix="react-select"
							/>
						</div>
					</div>
					<div className="flex justify-end gap-3">
						<button
							type="button"
							onClick={onClose}
							className="rounded border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
						>
							{t('notifications.cancelButton')}
						</button>
						<button
							type="submit"
							className="rounded bg-indigo-700 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-600 disabled:bg-indigo-300"
							disabled={isSaving}
						>
							{formMode === 'add'
								? isSaving
									? t('settingsPage.products.actions.saving')
									: t('settingsPage.products.actions.saveProduct')
								: isSaving
									? t('settingsPage.products.actions.saving')
									: t('settingsPage.products.actions.saveChanges')}
						</button>
					</div>
				</form>
			</div>
		</div>
	)
}
